import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';
import { useCreateEntity } from '@/hooks/useReference';
import type { BillableInput } from '@/api/reference';
import type { BillableParty } from '@/types';

/**
 * The one billing-party form. Manage billing parties edits with it, and every
 * picker that can end up with nobody to invoice (new invoice, the client row,
 * the client dialog, the invoice editor) adds with it, so a person never has
 * to leave what they were doing to create the payer.
 */
export function BillingPartyDialog({
  party,
  prefillName = '',
  saving,
  onClose,
  onSave
}: {
  /** `'new'` opens an empty form, a record opens it filled in, `null` closes it. */
  party: BillableParty | 'new' | null;
  /** Text typed into the picker before choosing "Create", used as the name. */
  prefillName?: string;
  saving?: boolean;
  onClose: () => void;
  onSave: (input: BillableInput) => void;
}) {
  const existing = party && party !== 'new' ? party : null;
  const [seeded, setSeeded] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    title: '',
    organization: '',
    address: '',
    phone: '',
    emails: ''
  });

  const seedKey = existing?.id ?? (party === 'new' ? `new:${prefillName}` : null);
  if (party && seeded !== seedKey) {
    setSeeded(seedKey);
    setForm({
      name: existing?.name ?? prefillName,
      title: existing?.title ?? '',
      organization: existing?.organization ?? '',
      address: existing?.address ?? '',
      phone: existing?.phone ?? '',
      emails: existing?.emails?.join(', ') ?? ''
    });
  }

  const valid = form.name.trim().length > 0;

  return (
    <Dialog
      open={Boolean(party)}
      onOpenChange={(v) => !v && !saving && onClose()}
      title={existing ? `Edit ${existing.name}` : 'New billing party'}
      description={existing ? undefined : 'Who the invoice is addressed and emailed to.'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={saving}
            onClick={() =>
              onSave({
                name: form.name.trim(),
                title: form.title || null,
                organization: form.organization || null,
                address: form.address || null,
                phone: form.phone || null,
                emails: form.emails
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
              })
            }
          >
            {existing ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoFocus
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Title">
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Managing Partner"
            />
          </Field>
          <Field label="Organization">
            <Input
              value={form.organization}
              onChange={(e) => setForm({ ...form, organization: e.target.value })}
            />
          </Field>
          <Field label="Phone">
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </Field>
          <Field label="Email(s)" hint="Comma separated.">
            <Input
              value={form.emails}
              onChange={(e) => setForm({ ...form, emails: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Address">
          <Textarea
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
        </Field>
      </div>
    </Dialog>
  );
}

/**
 * "Add new" for a billing-party picker: hand `open` to the Combobox's
 * `onAddNew`, render `dialog` next to it, and get the created record back.
 */
export function useAddBillingParty(onCreated: (party: BillableParty) => void): {
  open: (prefill?: string) => void;
  dialog: ReactNode;
} {
  const [prefill, setPrefill] = useState<string | null>(null);
  const create = useCreateEntity('billable');

  return {
    open: (text = '') => setPrefill(text),
    dialog: (
      <BillingPartyDialog
        party={prefill === null ? null : 'new'}
        prefillName={prefill ?? ''}
        saving={create.isPending}
        onClose={() => setPrefill(null)}
        onSave={async (input) => {
          let created: BillableParty;
          try {
            created = (await create.mutateAsync(input)) as BillableParty;
          } catch {
            // useCreateEntity already toasted; keep the form open.
            return;
          }
          setPrefill(null);
          onCreated(created);
        }}
      />
    )
  };
}
